import { ChevronLeftIcon, DownloadIcon, PlusIcon, SewingPinFilledIcon, TrashIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Spinner } from "@radix-ui/themes";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import MultiSelectComponent from "../../components/inputs/Select/MultiSelectComponent";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import SelectSearchableComponent from "../../components/inputs/Select/SelectSearchableComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useExportDocument } from "../../hooks/useExportDocument";
import { useGetAcademicYearQuery } from "../../hooks/useGetAcademicYearQuery";
import { useGetClassesQuery } from "../../hooks/useGetClassesQuery";
import { useGetCoursesQuery } from "../../hooks/useGetCoursesQuery";
import { useGetDepartamentsQuery } from "../../hooks/useGetDepartamentsQuery";
import { useGetFacultiesQuery } from "../../hooks/useGetFacultiesQuery";
import { useGetStudentClassesQuery } from "../../hooks/useGetStudentClassesQuery";
import { useGetSubscriptionsQuery } from "../../hooks/useGetSubscriptionsQuery";
import { useGetUniversitiesQuery } from "../../hooks/useGetUniversitiesQuery";
import { useLogoBase64 } from "../../hooks/useLogoBase64";
import { resetLoaderAvailable, setLoaderAvailable } from "../../redux/slices/loaderController";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { getPlanSubjects } from "../../services/school/PlanSubjects";
import { createStudentClass } from "../../services/school/student/createStudent";
import { updateStudentClass } from "../../services/school/student/updateStudent";
import { PagedResultsType } from "../../types/PagedResults";
import { StudentClassType, SubscriptionType } from "../../types/Student";
import { RegimeOptions, splitFullName } from "../../utils/studentUtils";
import { useGetSubscriptionSubjectsInClassQuery } from "../../hooks/useGetSubscriptionSubjectsInClassQuery";

type TemplateData = {
  type: string;
  outputFormat: string;
  data: Data;
  logoBase64: string;
}

type Data = {
  cargo: string;
  nome_completo: string;
  info: string;
  student_classes: Studentclass[];
}

type Studentclass = {
  ordem: string;
  codigo: string;
  nome: string;
  apelido: string;
}

const columnNamesMap: { [key: string]: string } = {
  student: 'Estudante',
};
export const ManageClassStudents = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [transferItem, setTransferItem] = useState<StudentClassType | null>(null);
  const [transferAllItems, setTransferAllItems] = useState<StudentClassType[] | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state: RootState) => state.login.userData);
  const { logoBase64 } = useLogoBase64();
  const { exportDocumentPdf: exportDocument } = useExportDocument();
  const classData = useSelector((state: RootState) => state.classes);
  const { idAcademicYear, regime, level, semester, idUniversity, idFaculty, idDepartament, idCourse, id, } = classData;
  const { data: classes, isLoading: isLoadingClasses, isError: isErrorClasses } = useGetClassesQuery
    ({
      PageNumber: 1, PageSize: 400,
      IdDepartament: idDepartament,
      IdCourse: idCourse,
      Semester: semester,
      IdAcademicYear: idAcademicYear
    },
      { enabled: !!idDepartament && !!idCourse && !!semester && !!idAcademicYear }
    );
  const defaultValues: StudentClassType = {
    idStudent: "estudanteSemId",
    idStudents: [],
    idClass: id!,
    status: true,
    reason: "none",
    transferedClass: null,
    IdAcademicYear: idAcademicYear,
    Regime: regime ?? 0,
    Level: level,
    Semester: semester,
    IdUniversity: idUniversity,
    IdFaculty: idFaculty,
    IdDepartament: idDepartament,
    IdCourse: idCourse,
  }
  const { control, handleSubmit, formState: { errors }, setValue, resetField, watch, getValues } = useForm({
    // resolver: yupResolver(AddStudentClassFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });


  const { data: studentsClassData, status: statusStudentsClass, isLoading: isLoadingStudentsClass, refetch: refreshStudentsClass } = useGetStudentClassesQuery({
    PageSize: 400,
    PageNumber: currentPage,
    IdClass: id,
  });

  const { data: academicYears, isLoading: isLoadingAcademicYears, isError: errorAcademicYears } = useGetAcademicYearQuery({ PageNumber: 1, PageSize: 400 });

  const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });

  const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400, IdUniversity: getValues('IdUniversity') });

  const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery(
    { PageNumber: 1, PageSize: 400, IdFaculty: getValues('IdFaculty') },
    { enabled: !!getValues('IdFaculty') }
  );
  const { data: courses, isLoading: isLoadingCourses, isError: errorCourses, refetch: refreshCourses } = useGetCoursesQuery({ PageNumber: 1, PageSize: 400, IdDepartament: getValues('IdDepartament') },
    { enabled: !!getValues('IdDepartament') }
  );

  const { data: planSubjectsData, isLoading: isLoadingPlanSubjectsData, isError: isErrorPlanSubjectsData, refetch: refreshPlanSubjects, status: planSubjectsStatus } =
    useQuery(
      ['getPlanSubjectsQuery'],
      async () => {
        try {
          const data = await getPlanSubjects({
            PageSize: 400,
            PageNumber: 1,
            Level: getValues('LevelSubject'),
            Semester: getValues('SemesterSubject'),
            IdAcademicYear: getValues('IdAcademicYear'),
            IdCourse: getValues('IdCourse')
          }
          );
          const responseData = { ...data }
          return responseData;
        } catch { }

      },
      {
        enabled: !!getValues('IdCourse') && !!getValues('LevelSubject') && !!getValues('SemesterSubject'),
        refetchOnWindowFocus: false,
        retry: false
      }
    );

  const filters = watch();
  // const { data: subscriptionsData, status: statusSubscriptions, isLoading: isLoadingSubscriptions, refetch: refreshSubscriptions } = useGetSubscriptionsQuery({
  //   PageSize: 600,
  //   PageNumber: currentPage,
  //   ...filters,
  //   HasClassForThisLevelYearSemester: true,
  // });

  const { data: subscriptionsData, status: statusSubscriptions, isLoading: isLoadingSubscriptions, refetch: refreshSubscriptions, isFetching } = useGetSubscriptionSubjectsInClassQuery({
    PageSize: 600,
    PageNumber: currentPage,
    ...filters,
    HasClassForThisLevelYearSemester: true,
  });

  //   const { data: subscriptionSubjectsInClassData, status: statusSubscriptionSubjectsInClass, isLoading: isLoadingSubscriptionSubjectsInClass, refetch: refreshSubscriptionSubjectsInClass } = useGetSubscriptionSubjectsInClassQuery({
  //   PageSize: 600,
  //   PageNumber: currentPage,
  //   ...filters,
  //   HasClassForThisLevelYearSemester: true,
  // });

  const [sortedStudentsClass, setSortedStudentsClass] = useState<StudentClassType[] | null>(null);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<PagedResultsType<SubscriptionType> | null>(null);

  useEffect(() => {
    if (studentsClassData) {
      const sortedData = [...studentsClassData.items].sort((a, b) =>
        (a.student || "").localeCompare(b.student || "")
      );
      setSortedStudentsClass(sortedData);
    }
  }, [studentsClassData]);

  useEffect(() => {
    if (subscriptionsData && sortedStudentsClass) {
      const sortedSubscriptionsData = [...subscriptionsData.items].sort((a, b) =>
        (a.student || "").localeCompare(b.student || "")
      );

      const studentIdsInClass = sortedStudentsClass.map(student => student.idStudent);

      const filteredItems = sortedSubscriptionsData.filter(
        subscription => !studentIdsInClass.includes(subscription.idStudent ?? "")
      );

      setFilteredSubscriptions({
        ...subscriptionsData,
        items: filteredItems,
      });

    }
  }, [subscriptionsData, sortedStudentsClass]);

  const mutation = useMutation(createStudentClass, {
    onSuccess: () => {
      toast.success("Estudante adicionado!");
    },
    onError: () => {
      toast.error("Erro ao adicionar estudante.");
    }
  });
  const mutationUpdate = useMutation(updateStudentClass, {
    onSuccess: () => {
      toast.success("Ação realizada!");
    },
    onError: () => {
      toast.error("Ação não realizada.");
    }
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      data.transferedClass = null;
      const idsStudents = data.idStudents.filter(id => !isEmpty(id));
      await Promise.all(idsStudents.map((id) => {
        return mutation.mutateAsync({
          ...data,
          idStudent: id
        })
      }))
      refreshStudentsClass();
      resetField("transferedClass");
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  })
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };


  const openTransferDialog = (c: StudentClassType) => {

    if (c.isTransfered) {
      toast.error("Estudante transferido");
      return;
    }
    setTransferItem(c);
  };
  const handleTransfer = async (c: any) => {
    c.isTransfered = true;
    c.transferedClass = classData?.id;
    c.idClass = getValues('transferedClass');
    try {
      await mutationUpdate.mutateAsync(c)
      refreshStudentsClass();
    } catch (ex) {

    }
  };
  const confirmTransfer = async () => {
    if (getValues('transferedClass') === classData?.id) {
      toast.error("Estudante já está nesta turma");
      return
    }
    if (isEmpty(getValues('transferedClass'))) {
      toast.error("Selecione uma turma");
      return
    }
    if (transferItem) {
      await handleTransfer(transferItem!);
      resetField("transferedClass");
    }
  };

  const handleDelete = async (c: any) => {
    c.status = false;
    try {
      await mutationUpdate.mutateAsync(c)
      refreshStudentsClass();

    } catch (ex) {

    }
  };

  const handleDeleteAll = async (c: any[]) => {
    try {

      for (const item of c) {

        item.status = false;


        await mutationUpdate.mutateAsync(item);
      }

      refreshStudentsClass();

    } catch (ex) {
      console.error("Erro ao eliminar todos os registos:", ex);
    }
  };

  const openTransferAllDialog = async (c: any[]) => {
    setTransferAllItems(c);
  };

  const confirmTransferAll = async () => {
    if (getValues('transferedClass') === classData?.id) {
      toast.error("Estudantes já estão nesta turma");
      return
    }
    if (isEmpty(getValues('transferedClass'))) {
      toast.error("Selecione uma turma");
      return
    }
    if (transferAllItems) {

      try {

        for (const item of transferAllItems) {
          await handleTransfer(item!)
        }
        resetField("transferedClass");
      } catch (ex) {
        console.error("Erro ao transferir todos os itens:", ex);
      }
    }
  };

  const optionsForMassiveItems = [
    {
      label: 'Eliminar',
      onClick: handleDeleteAll,
      icon: <TrashIcon />
    },
    {
      label: 'Transferir',
      onClick: openTransferAllDialog,
      icon: <SewingPinFilledIcon />
    },

  ];
  const options = studentsClassData?.items.map((studentClass) => [
    {
      label: 'Eliminar',
      onClick: () => handleDelete(studentClass),
      icon: <TrashIcon />
    },
    {
      label: 'Transferir',
      onClick: () => openTransferDialog(studentClass),
      icon: <SewingPinFilledIcon />
    },

  ]) || [];


  const handleOnClickPrint = async (event: any) => {

    try {
      dispatch(setLoaderAvailable(true))

      event.preventDefault();
      if (studentsClassData && studentsClassData.items && studentsClassData.items?.length > 0) {
        const data: TemplateData = {
          type: "STUDENT_CLASSES",
          outputFormat: "DOCX",
          data: {
            cargo: Array.isArray(userData?.Role) ? userData?.Role.join(", ") : userData?.Role ?? "",
            nome_completo: userData.Fullname ?? "",
            info: `${classData?.description} - ${classData?.academicYear} - ${RegimeOptions[classData?.regime ?? "1"] ?? ""} - ${classData?.level ?? ""}º Ano - ${classData?.semester ?? ""}º Semestre - ${classData?.course ?? ""} - ${classData?.departament ?? ""}`,
            student_classes: studentsClassData.items.map((i, index) => {
              const { firstName, lastName } = splitFullName(i.student);
              return {
                ordem: (index + 1).toString(),
                codigo: i.studentCode ?? "",
                nome: firstName,
                apelido: lastName
              };
            })
          },
          logoBase64: logoBase64 ?? ''
        }
        await exportDocument(data, 'Lista_Estudantes_Turma_');
        return;
      }
      toast.error("Sem estudantes na turma");

    } catch (error) {
      toast.error("Ocorreu um erro ao extrair a lista de estudantes.")
    } finally {
      dispatch(setLoaderAvailable(false))
    }

  }

  const isEmptyWatchTransferedClass = isEmpty(watch('transferedClass'));

  const handleChangeStudents = (value: string[]): void => {
    setValue('idStudents', value);
  }



  useEffect(() => {
    if (academicYears?.items && academicYears.items.length > 0) {
      if (!getValues('IdAcademicYear')) {
        const mostRecentYear = academicYears.items[academicYears.items.length - 1];
        setValue('IdAcademicYear', mostRecentYear.id);
      }
    }
  }, [academicYears, setValue, getValues]);


  const handleLevelChange = async (e: any) => {
    var level = e.target.value;
    setValue("Level", level);
  }
  const handleSemesterChange = async (e: any) => {
    var semester = e.target.value;
    setValue("Semester", semester);
  }
  const handleLevelSubjectChange = async (e: any) => {
    var level = e.target.value;
    setValue("LevelSubject", level);
    refreshPlanSubjects();
  }
  const handleSemesterSubjectChange = async (e: any) => {
    var semester = e.target.value;
    setValue("SemesterSubject", semester);
    refreshPlanSubjects();
  }
  const handleUniversityChange = (id: string) => {
    setValue('IdUniversity', id);
    setValue('IdFaculty', '');
    setValue('IdDepartament', '');
    setValue('IdCourse', '');
    refetchFaculties();
  };

  const handleFacultyChange = (idFaculty: string) => {
    setValue('IdFaculty', idFaculty);
    refreshDepartaments();
  };

  const handleDepartamentChange = (idDepartament: string) => {
    setValue('IdDepartament', idDepartament);
    refreshCourses();
  };

  const handleCourseChange = async (idCourse: string) => {
    setValue('IdCourse', idCourse);
    refreshPlanSubjects()
  }

 useEffect(() => {
  if (isFetching) {
    dispatch(resetLoaderAvailable());
    dispatch(setLoaderAvailable(true));
  } else {
    dispatch(setLoaderAvailable(false));
  }
}, [isFetching, dispatch]);

  return (
    <Card size='3'>
      <Flex direction="row" gap='6'>
        <Flex direction="column" gap="5" style={{ flex: 1 }}>
          <form onSubmit={onSubmit} noValidate>
            <Flex direction="column" gap='4'>
              <SelectComponent
                label="Turma"
                options={[{ value: id!, label: classData.description }]}
                errors={errors}
                control={control}
                name="idClass"
                required={true}
              />

              <Flex gap="4" direction="column">
                <RadioGroupComponent name="Regime" label="Regime" control={control} errors={errors} style={{ width: '100%' }}
                  options={[
                    { value: "1", label: "Laboral" }
                    , { value: "2", label: "Pós Laboral" }
                    , { value: "3", label: "Distância" }
                  ]}
                  fieldProps={{
                    style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
                  }}
                />
                <Flex gap='4'>
                  <TextFieldComponent
                    name="Level"
                    label="Ano"
                    control={control}
                    errors={errors}
                    fieldProps={{ placeholder: 'Ano', onChange: handleLevelChange, type: 'number' }} style={{ width: '100%' }}
                  />
                  <TextFieldComponent
                    name="Semester"
                    label="Semestre"
                    control={control}
                    errors={errors}
                    fieldProps={{ placeholder: 'Semestre', onChange: handleSemesterChange, type: 'number' }} style={{ width: '100%' }}

                  />
                </Flex>
                <Flex gap='4' direction="column">
                  <SelectComponent
                    name="IdUniversity"
                    label="Instituição de Ensino Superior"
                    control={control}
                    errors={errors}
                    style={{ width: '100%' }}
                    options={universities?.items.map(u => ({
                      value: u.id ?? "",
                      label: u.description ?? ""
                    }))}
                    itemsLoading={isLoadingUniversities}
                    itemsError={errorUniversities}
                    onSelect={handleUniversityChange}
                    required={true}
                  />
                  <SelectComponent name="IdFaculty" label='Unidade orgânica' control={control} errors={errors} style={{ width: '100%' }}
                    options={faculties?.items.map(faculty => ({
                      value: faculty.id ?? "",
                      label: faculty.description ?? ""
                    }))}
                    onSelect={handleFacultyChange}
                    itemsError={errorFaculties}
                    itemsLoading={isLoadingFaculties}
                  />
                  <SelectComponent name="IdDepartament" label='Departamento' control={control} errors={errors} style={{ width: '100%' }}
                    options={departaments?.items.map(departament => ({
                      value: departament.id ?? "",
                      label: departament.description ?? ""
                    }))}
                    onSelect={handleDepartamentChange}
                    itemsError={errorDepartaments}
                    itemsLoading={isLoadingDepartaments}
                  />

                </Flex>
                <Flex gap='4' direction='column'>


                  <SelectComponent name="IdCourse" label='Curso' control={control} errors={errors} style={{ width: '100%' }}
                    options={courses?.items.map(course => ({
                      value: course.id ?? "",
                      label: course.name ?? ""
                    }))}
                    onSelect={
                      handleCourseChange
                    }
                    itemsError={errorCourses}
                    itemsLoading={isLoadingCourses}

                  />

                  <Flex gap='4'>
                    <TextFieldComponent
                      name="LevelSubject"
                      label="Ano da disciplina"
                      control={control}
                      errors={errors}
                      fieldProps={{ placeholder: 'Ano', onChange: handleLevelSubjectChange, type: 'number' }} style={{ width: '100%' }}
                    />
                    <TextFieldComponent
                      name="SemesterSubject"
                      label="Semestre da disciplina"
                      control={control}
                      errors={errors}
                      fieldProps={{ placeholder: 'Semestre', onChange: handleSemesterSubjectChange, type: 'number' }} style={{ width: '100%' }}

                    />
                  </Flex>
                  <SelectComponent
                    name="IdSubject"
                    label="Disciplina"
                    control={control}
                    errors={errors}
                    options={planSubjectsData?.items.map(subject => ({
                      value: subject.idSubject ?? "",
                      label: subject.subject ?? ""
                    }))}
                    style={{ width: '100%' }}
                    itemsError={isErrorPlanSubjectsData}
                    itemsLoading={isLoadingPlanSubjectsData}
                  />
                </Flex>
              </Flex>


              <MultiSelectComponent
                name={"idStudents"}
                label="Estudantes"
                options={filteredSubscriptions?.items.map((subscription) => ({
                  value: subscription?.idStudent ?? "",
                  label: `${subscription?.student} - ${subscription?.studentCode} - ${subscription.level}º Ano`
                })) ?? []}
                errors={errors}
                style={{ width: '100%' }}
                control={control}
                required={true}
                onSelect={handleChangeStudents}
                activateSelectAllButton={true}
                setValue={setValue}
              />

              {/* <SelectSearchableComponent
                label="Estudantes"
                options={subscriptionsData?.items.map((subscription) => ({
                  value: subscription?.idStudent ?? "",
                  label: `${subscription?.student} - ${subscription?.studentCode} - ${subscription.level} `
                }))}
                errors={errors}
                control={control}
                name="idStudent"
                //  onSelect={handleStudentChange}
                required={true}
              /> */}
              <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                <Button type="button" variant="soft" color="gray" onClick={() => navigate(routes.classes)}>
                  <ChevronLeftIcon /> Voltar
                </Button>

                <Button type="button" onClick={handleOnClickPrint} variant='surface'>
                  <DownloadIcon /> Extrair
                </Button>

                <Button type="submit" variant="surface" disabled={mutation.isLoading}>
                  {mutation.isLoading ? <Spinner loading /> : <PlusIcon />}
                  Adicionar estudantes
                </Button>

              </Flex>

            </Flex>
          </form>
        </Flex>
        <Flex direction="column" gap="4" style={{ flex: 1 }}>
          <>
            <Lists
              status={statusStudentsClass}
              TableComponent={() => <TableComponent
                columns={Object.keys(columnNamesMap)}
                items={sortedStudentsClass ?? studentsClassData?.items}
                options={options}
                columnNamesMap={columnNamesMap}
                totalPages={studentsClassData?.totalPages || 1}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                control={control}
                errors={errors}
                setValue={setValue}
                getValues={getValues}
                optionsForMassiveItems={optionsForMassiveItems}
              />} />
            <ConfirmAlertDialog
              open={!!transferItem}
              descriptionComponent={
                <>
                  <SelectSearchableComponent
                    name="transferedClass"
                    label="Turma"
                    options={classes?.items.filter(i => i.id !== classData?.id).map((i) => ({
                      value: i?.id ?? "",
                      label: `${i?.description}`
                    }))}
                    errors={errors}
                    control={control}
                    required={true}
                  />
                </>
              }
              disabledConfirm={isEmptyWatchTransferedClass}
              onOpenChange={(open) => !open && setTransferItem(null)}
              title="Transferir estudante de turma"
              description="Esta operação é irreversível. Seleccione a turma de destino."
              confirmLabel="Transferir"
              item={transferItem}
              onConfirm={confirmTransfer}
              onCancel={() => setTransferItem(null)} />

            <ConfirmAlertDialog
              open={!!transferAllItems}
              descriptionComponent={
                <>
                  <SelectSearchableComponent
                    name="transferedClass"
                    label="Turma"
                    options={classes?.items.filter(i => i.id !== classData?.id).map((i) => ({
                      value: i?.id ?? "",
                      label: `${i?.description}`
                    }))}
                    errors={errors}
                    control={control}
                    required={true}
                  />
                </>
              }
              disabledConfirm={isEmptyWatchTransferedClass}
              onOpenChange={(open) => !open && setTransferAllItems(null)}
              title="Transferir estudantes de turma"
              description="Esta operação é irreversível. Seleccione a turma de destino."
              confirmLabel="Transferir"
              item={transferAllItems}
              onConfirm={confirmTransferAll}
              onCancel={() => setTransferAllItems(null)} />
          </>
        </Flex>
      </Flex>
    </Card>
  )
}